import { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Copy, Plus, Trash2, Globe, Play, Loader2, Check, X, Edit2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  useEndpoints, 
  useCreateEndpoint, 
  useUpdateEndpoint, 
  useDeleteEndpoint,
  useSyncToApisix,
  useSyncEndpoint,
  useRemoveEndpointFromGateway,
  useTestEndpoint 
} from '@/hooks/useEndpoints';
import { Endpoint, EndpointCreateData, EndpointTestRequest, RateLimits } from '@/services/endpoints.service';

interface ProductEndpointsTabProps {
  productId: string;
}

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

const getGatewayPath = (endpoint: Endpoint | null | undefined): string => {
  if (!endpoint) return '';
  const path = endpoint.gateway_path ?? endpoint.gatewayPath ?? endpoint.path;
  return typeof path === 'string' ? path : '';
};

const getOriginalUrl = (endpoint: Endpoint | null | undefined): string => {
  if (!endpoint) return '';
  const original = endpoint.original_url ?? endpoint.originalUrl;
  return typeof original === 'string' ? original : '';
};

const getMethods = (endpoint: Endpoint | null | undefined): string[] => {
  if (!endpoint) return [];
  if (endpoint.methods && endpoint.methods.length > 0) {
    return endpoint.methods;
  }
  return endpoint.method ? [endpoint.method] : [];
};

const getRateLimits = (endpoint: Endpoint | null | undefined): RateLimits | undefined => {
  if (!endpoint) return undefined;
  const limits = endpoint.rate_limits ?? endpoint.rateLimits;
  if (limits && Object.keys(limits).length > 0) {
    return limits as RateLimits;
  }
  if (endpoint.rate_limit) {
    return { per_minute: endpoint.rate_limit };
  }
  return undefined;
};

const getIsActive = (endpoint: Endpoint | null | undefined): boolean => {
  if (!endpoint) return false;
  const value = endpoint.is_active ?? endpoint.isActive;
  return typeof value === 'boolean' ? value : false;
};

const getApisixStatus = (endpoint: Endpoint | null | undefined): 'pending' | 'synced' | 'error' | undefined => {
  if (!endpoint) return undefined;
  return (endpoint.apisix_sync_status ?? endpoint.apisixSyncStatus ?? endpoint.sync_status) as 'pending' | 'synced' | 'error' | undefined;
};

const getSkipApisixSync = (endpoint: Endpoint | null | undefined): boolean => {
  if (!endpoint) return false;
  const value = endpoint.skip_apisix_sync ?? endpoint.skipApisixSync;
  return typeof value === 'boolean' ? value : false;
};

const getPrimaryMethod = (endpoint: Endpoint | null | undefined): string => {
  const methods = getMethods(endpoint);
  return methods[0] || 'GET';
};

export default function ProductEndpointsTab({ productId }: ProductEndpointsTabProps) {
  const { t } = useTranslation();

  // Backend data hooks
  const { data: endpointsResponse, isLoading, error, refetch } = useEndpoints(productId);
  const createEndpointMutation = useCreateEndpoint();
  const updateEndpointMutation = useUpdateEndpoint();
  const deleteEndpointMutation = useDeleteEndpoint();
  const syncToApisixMutation = useSyncToApisix();
  const syncEndpointMutation = useSyncEndpoint();
  const removeEndpointMutation = useRemoveEndpointFromGateway();
  const testEndpointMutation = useTestEndpoint();

  const normalizedEndpoints = useMemo(() => {
    const rawEndpoints = endpointsResponse?.data ?? [];
    return rawEndpoints.map((endpoint) => ({
      id: endpoint.id,
      name: typeof endpoint.name === 'string' ? endpoint.name : '',
      gatewayPath: getGatewayPath(endpoint),
      originalUrl: getOriginalUrl(endpoint),
      methods: getMethods(endpoint),
      rateLimits: getRateLimits(endpoint),
      description: typeof endpoint.description === 'string' ? endpoint.description : (endpoint.description ?? '') as string,
      isActive: getIsActive(endpoint),
      apisixSyncStatus: getApisixStatus(endpoint),
      skipGatewaySync: getSkipApisixSync(endpoint),
      raw: endpoint,
    }));
  }, [endpointsResponse?.data]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [testingEndpoint, setTestingEndpoint] = useState<Endpoint | null>(null);
  const [testRequest, setTestRequest] = useState({
    method: 'GET',
    headers: [{ key: 'Content-Type', value: 'application/json' }],
    body: '',
    apiKey: '',
    useTestKey: false
  });
  const [testResponse, setTestResponse] = useState<{
    status: number;
    statusText: string;
    headers: Record<string, string>;
    body: unknown;
    time: number;
  } | null>(null);
  const [isTestLoading, setIsTestLoading] = useState(false);
  const testingGatewayPath = getGatewayPath(testingEndpoint);
  const testingMethods = getMethods(testingEndpoint);
  const testingMethodOptions = testingMethods.length > 0 ? testingMethods : HTTP_METHODS;

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingEndpoint, setEditingEndpoint] = useState<Endpoint | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [endpointToDelete, setEndpointToDelete] = useState<string | null>(null);
  const [syncingEndpointId, setSyncingEndpointId] = useState<string | null>(null);
  const [removingEndpointId, setRemovingEndpointId] = useState<string | null>(null);
  const [newEndpoint, setNewEndpoint] = useState<Partial<EndpointCreateData>>({
    gateway_path: '',
    original_url: '',
    methods: [],
    rate_limits: undefined,
    description: '',
    is_active: true,
    skip_apisix_sync: false
  });

  const handleAddEndpoint = async () => {
    const endpointData: EndpointCreateData = {
      gateway_path: newEndpoint.gateway_path || '',
      original_url: newEndpoint.original_url || '',
      methods: newEndpoint.methods || [],
      rate_limits: newEndpoint.rate_limits,
      description: newEndpoint.description || '',
      is_active: newEndpoint.is_active ?? true,
      skip_apisix_sync: newEndpoint.skip_apisix_sync ?? false,
    };

    try {
      await createEndpointMutation.mutateAsync({ productId, data: endpointData });
      setIsAddModalOpen(false);
      setNewEndpoint({
        gateway_path: '',
        original_url: '',
        methods: [],
        rate_limits: undefined,
        description: '',
        is_active: true,
        skip_apisix_sync: false
      });
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  const handleDeleteEndpoint = (id: string) => {
    setEndpointToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (endpointToDelete) {
      try {
        await deleteEndpointMutation.mutateAsync({ productId, endpointId: endpointToDelete });
        setEndpointToDelete(null);
      } catch (error) {
        // Error handling is done in the mutation
      }
    }
    setDeleteConfirmOpen(false);
  };

  const handleCopyGatewayPath = (path: string) => {
    if (!path) return;
    const fullPath = `https://api.florentin.com${path}`;
    navigator.clipboard.writeText(fullPath);
  };

  const handleTestEndpoint = (endpoint: Endpoint) => {
    setTestingEndpoint(endpoint);
    setTestRequest({
      method: getPrimaryMethod(endpoint),
      headers: [{ key: 'Content-Type', value: 'application/json' }],
      body: '',
      apiKey: '',
      useTestKey: false
    });
    setTestResponse(null);
    setIsTestModalOpen(true);
  };

  const handleExecuteTest = async () => {
    if (!testingEndpoint) return;

    setIsTestLoading(true);
    try {
      const testData: EndpointTestRequest = {
        method: testRequest.method,
        headers: testRequest.headers.reduce((acc, header) => {
          if (header.key && header.value) {
            acc[header.key] = header.value;
          }
          return acc;
        }, {} as Record<string, string>),
        body: testRequest.body,
        api_key: testRequest.apiKey,
      };

      const response = await testEndpointMutation.mutateAsync({
        productId,
        endpointId: testingEndpoint.id,
        testData
      });

      setTestResponse(response);
    } catch (error) {
      // Error handling is done in the mutation
    } finally {
      setIsTestLoading(false);
    }
  };

  const addHeader = () => {
    setTestRequest({
      ...testRequest,
      headers: [...testRequest.headers, { key: '', value: '' }]
    });
  };

  const updateHeader = (index: number, field: 'key' | 'value', value: string) => {
    const newHeaders = [...testRequest.headers];
    newHeaders[index][field] = value;
    setTestRequest({ ...testRequest, headers: newHeaders });
  };

  const removeHeader = (index: number) => {
    setTestRequest({
      ...testRequest,
      headers: testRequest.headers.filter((_, i) => i !== index)
    });
  };

  const handleSyncToAPISIX = async () => {
    try {
      await syncToApisixMutation.mutateAsync(productId);
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  const toggleMethod = (method: string) => {
    const methods = newEndpoint.methods || [];
    if (methods.includes(method)) {
      setNewEndpoint({ ...newEndpoint, methods: methods.filter(m => m !== method) });
    } else {
      setNewEndpoint({ ...newEndpoint, methods: [...methods, method] });
    }
  };

  const handleEditEndpoint = (endpoint: Endpoint) => {
    setEditingEndpoint(endpoint);
    setNewEndpoint({
      gateway_path: getGatewayPath(endpoint),
      original_url: getOriginalUrl(endpoint),
      methods: getMethods(endpoint),
      rate_limits: getRateLimits(endpoint),
      description: typeof endpoint.description === 'string' ? endpoint.description : undefined,
      is_active: getIsActive(endpoint),
      skip_apisix_sync: getSkipApisixSync(endpoint)
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateEndpoint = async () => {
    if (!editingEndpoint) return;
    
    const updateData = {
      gateway_path: newEndpoint.gateway_path || '',
      original_url: newEndpoint.original_url || '',
      methods: newEndpoint.methods || [],
      rate_limits: newEndpoint.rate_limits,
      description: newEndpoint.description || '',
      is_active: newEndpoint.is_active ?? true,
      skip_apisix_sync: newEndpoint.skip_apisix_sync ?? false,
    };

    try {
      await updateEndpointMutation.mutateAsync({
        productId,
        endpointId: editingEndpoint.id,
        data: updateData
      });

      setIsEditModalOpen(false);
      setEditingEndpoint(null);
      setNewEndpoint({
        gateway_path: '',
        original_url: '',
        methods: [],
        rate_limits: undefined,
        description: '',
        is_active: true,
        skip_apisix_sync: false
      });
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  const handleSyncEndpoint = async (endpointId: string) => {
    setSyncingEndpointId(endpointId);
    try {
      await syncEndpointMutation.mutateAsync({ productId, endpointId });
    } catch (error) {
      // toast handled in hook
    } finally {
      setSyncingEndpointId(null);
    }
  };

  const handleRemoveEndpointFromGateway = async (endpointId: string) => {
    setRemovingEndpointId(endpointId);
    try {
      await removeEndpointMutation.mutateAsync({ productId, endpointId });
    } catch (error) {
      // toast handled in hook
    } finally {
      setRemovingEndpointId(null);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{t('products.apiGatewayEndpoints')}</CardTitle>
                <CardDescription>
                  {t('products.configureEndpoints')}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" disabled>
                  <Globe className="mr-2 h-4 w-4" />
                  {t('products.syncToGateway')}
                </Button>
                <Button disabled>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('products.addEndpoint')}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="h-4 bg-muted rounded animate-pulse" />
              <div className="h-4 bg-muted rounded animate-pulse" />
              <div className="h-4 bg-muted rounded animate-pulse" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('products.apiGatewayEndpoints')}</CardTitle>
            <CardDescription>
              {t('products.configureEndpoints')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <p className="text-destructive mb-4">
                {t('products.errors.failedToLoadEndpoints', 'Failed to load endpoints')}
              </p>
              <Button onClick={() => refetch()} variant="outline">
                <Loader2 className="mr-2 h-4 w-4" />
                {t('common.retry')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('products.apiGatewayEndpoints')}</CardTitle>
              <CardDescription>
                {t('products.configureEndpoints')}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={handleSyncToAPISIX}
                disabled={syncToApisixMutation.isPending}
              >
                {syncToApisixMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Globe className="mr-2 h-4 w-4" />
                )}
                {t('products.syncToGateway')}
              </Button>
              <Button onClick={() => setIsAddModalOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                {t('products.addEndpoint')}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('products.gatewayPath')}</TableHead>
                <TableHead>{t('products.originalUrl')}</TableHead>
                <TableHead>{t('products.methods')}</TableHead>
                <TableHead>{t('products.rateLimits')}</TableHead>
                <TableHead>{t('common.status')}</TableHead>
                <TableHead className="text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {normalizedEndpoints.map(({ id, gatewayPath, originalUrl, methods, rateLimits, description, isActive, apisixSyncStatus, skipGatewaySync, raw }) => (
                <TableRow key={id}>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm">{gatewayPath || '—'}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopyGatewayPath(gatewayPath)}
                          title={t('products.copyGatewayUrl')}
                          className="h-6 px-1"
                          disabled={!gatewayPath}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                      {description && (
                        <div className="text-xs text-muted-foreground">{description}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="font-mono text-xs break-all">{originalUrl || '—'}</div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      {methods.length > 0 ? methods.map(method => (
                        <Badge key={method} variant="secondary" className="text-xs">
                          {method}
                        </Badge>
                      )) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-xs space-y-0.5">
                      {rateLimits ? (
                        <>
                          {rateLimits.per_minute !== undefined && (
                            <div>
                              {rateLimits.per_minute}
                              {t('products.perMinSuffix')}
                            </div>
                          )}
                          {rateLimits.per_hour !== undefined && (
                            <div>
                              {rateLimits.per_hour}
                              {t('products.perHourSuffix')}
                            </div>
                          )}
                          {rateLimits.per_day !== undefined && (
                            <div>
                              {rateLimits.per_day}
                              {t('products.perDaySuffix')}
                            </div>
                          )}
                        </>
                      ) : (
                        <div>—</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant={isActive ? 'default' : 'secondary'}
                        className="text-xs"
                      >
                        {isActive ? t('common.active') : t('common.inactive')}
                      </Badge>
                      {skipGatewaySync && (
                        <Badge
                          variant="secondary"
                          className="text-xs bg-amber-100 text-amber-800 border border-amber-200"
                        >
                          {t('products.apisixSkipped', 'APISIX skipped')}
                        </Badge>
                      )}
                      {!skipGatewaySync && (
                        <>
                          {apisixSyncStatus === 'synced' && (
                            <Badge variant="outline" className="text-xs text-green-600">
                              {t('common.synced')}
                            </Badge>
                          )}
                          {apisixSyncStatus === 'pending' && (
                            <Badge variant="outline" className="text-xs text-yellow-600">
                              {t('common.pending')}
                            </Badge>
                          )}
                          {apisixSyncStatus === 'error' && (
                            <Badge variant="outline" className="text-xs text-red-600">
                              {t('common.error')}
                            </Badge>
                          )}
                        </>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSyncEndpoint(id)}
                        title={t('products.syncEndpoint', 'Sync endpoint')}
                        disabled={syncingEndpointId === id}
                      >
                        {syncingEndpointId === id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Globe className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveEndpointFromGateway(id)}
                        title={t('products.removeEndpointFromGateway', 'Remove from gateway')}
                        disabled={removingEndpointId === id}
                      >
                        {removingEndpointId === id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <X className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTestEndpoint(raw)}
                        title={t('products.testEndpoint')}
                      >
                        <Play className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditEndpoint(raw)}
                        title={t('products.editEndpoint')}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteEndpoint(id)}
                        title={t('products.deleteEndpoint')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {normalizedEndpoints.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              {t('products.noEndpointsConfigured')}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Endpoint Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('products.addNewEndpoint')}</DialogTitle>
            <DialogDescription>
              {t('products.configureEndpoints')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="gateway-path">{t('products.gatewayPath')}</Label>
              <Input
                id="gateway-path"
                placeholder={t('products.gatewayPathPlaceholder')}
                value={newEndpoint.gateway_path}
                onChange={(e) => setNewEndpoint({ ...newEndpoint, gateway_path: e.target.value })}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('products.publicPathDescription')}
              </p>
            </div>

            <div>
              <Label htmlFor="original-url">{t('products.originalUrl')}</Label>
              <Input
                id="original-url"
                placeholder={t('products.endpointUrlPlaceholder')}
                value={newEndpoint.original_url}
                onChange={(e) => setNewEndpoint({ ...newEndpoint, original_url: e.target.value })}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('products.yourActualApiEndpoint')}
              </p>
            </div>

            <div>
              <Label>{t('products.httpMethods')}</Label>
              <div className="flex gap-3 mt-2">
                {HTTP_METHODS.map(method => (
                  <label key={method} className="flex items-center gap-2">
                    <Checkbox
                      checked={newEndpoint.methods?.includes(method) || false}
                      onCheckedChange={() => toggleMethod(method)}
                    />
                    <span className="text-sm">{method}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label>{t('products.rateLimitsOptional')}</Label>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div>
                  <Label htmlFor="rate-minute" className="text-xs">{t('products.perMinute')}</Label>
                  <Input
                    id="rate-minute"
                    type="number"
                    placeholder="60"
                    value={newEndpoint.rate_limits?.per_minute || ''}
                    onChange={(e) => setNewEndpoint({ 
                      ...newEndpoint, 
                      rate_limits: { 
                        ...newEndpoint.rate_limits, 
                        per_minute: e.target.value ? parseInt(e.target.value) : undefined 
                      }
                    })}
                  />
                </div>
                <div>
                  <Label htmlFor="rate-hour" className="text-xs">{t('products.perHour')}</Label>
                  <Input
                    id="rate-hour"
                    type="number"
                    placeholder="1000"
                    value={newEndpoint.rate_limits?.per_hour || ''}
                    onChange={(e) => setNewEndpoint({ 
                      ...newEndpoint, 
                      rate_limits: { 
                        ...newEndpoint.rate_limits, 
                        per_hour: e.target.value ? parseInt(e.target.value) : undefined 
                      }
                    })}
                  />
                </div>
                <div>
                  <Label htmlFor="rate-day" className="text-xs">{t('products.perDay')}</Label>
                  <Input
                    id="rate-day"
                    type="number"
                    placeholder="10000"
                    value={newEndpoint.rate_limits?.per_day || ''}
                    onChange={(e) => setNewEndpoint({ 
                      ...newEndpoint, 
                      rate_limits: { 
                        ...newEndpoint.rate_limits, 
                        per_day: e.target.value ? parseInt(e.target.value) : undefined 
                      }
                    })}
                  />
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="skip-apisix-sync"
                checked={newEndpoint.skip_apisix_sync || false}
                onCheckedChange={(checked) => setNewEndpoint({ ...newEndpoint, skip_apisix_sync: !!checked })}
              />
              <div className="space-y-1">
                <Label htmlFor="skip-apisix-sync" className="font-medium">
                  {t('products.skipGatewaySync', 'Skip API gateway sync')}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t('products.skipGatewaySyncDescription', 'Store this endpoint without queuing an APISIX sync job')}
                </p>
              </div>
            </div>

            <div>
              <Label htmlFor="description">{t('products.descriptionOptional')}</Label>
              <Textarea
                id="description"
                placeholder={t('products.describeEndpoint')}
                value={newEndpoint.description}
                onChange={(e) => setNewEndpoint({ ...newEndpoint, description: e.target.value })}
                className="mt-1"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleAddEndpoint}
              disabled={!newEndpoint.gateway_path || !newEndpoint.original_url || !newEndpoint.methods?.length || createEndpointMutation.isPending}
            >
              {createEndpointMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              {t('products.addEndpoint')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Endpoint Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('products.editApiEndpoint')}</DialogTitle>
            <DialogDescription>
              {t('products.updateEndpointConfiguration')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-gateway-path">{t('products.gatewayPath')}</Label>
              <Input
                id="edit-gateway-path"
                placeholder={t('products.gatewayPathPlaceholder')}
                value={newEndpoint.gateway_path}
                onChange={(e) => setNewEndpoint({ ...newEndpoint, gateway_path: e.target.value })}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('products.publicPathDescription')}
              </p>
            </div>

            <div>
              <Label htmlFor="edit-original-url">{t('products.originalUrl')}</Label>
              <Input
                id="edit-original-url"
                placeholder={t('products.endpointUrlPlaceholder')}
                value={newEndpoint.original_url}
                onChange={(e) => setNewEndpoint({ ...newEndpoint, original_url: e.target.value })}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('products.yourActualApiEndpoint')}
              </p>
            </div>

            <div>
              <Label>{t('products.httpMethods')}</Label>
              <div className="flex gap-3 mt-2">
                {HTTP_METHODS.map(method => (
                  <label key={method} className="flex items-center gap-2">
                    <Checkbox
                      checked={newEndpoint.methods?.includes(method) || false}
                      onCheckedChange={() => toggleMethod(method)}
                    />
                    <span className="text-sm">{method}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label>{t('products.rateLimitsOptional')}</Label>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div>
                  <Label htmlFor="edit-rate-minute" className="text-xs">{t('products.perMinute')}</Label>
                  <Input
                    id="edit-rate-minute"
                    type="number"
                    placeholder="60"
                    value={newEndpoint.rate_limits?.per_minute || ''}
                    onChange={(e) => setNewEndpoint({ 
                      ...newEndpoint, 
                      rate_limits: { 
                        ...newEndpoint.rate_limits, 
                        per_minute: e.target.value ? parseInt(e.target.value) : undefined 
                      }
                    })}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-rate-hour" className="text-xs">{t('products.perHour')}</Label>
                  <Input
                    id="edit-rate-hour"
                    type="number"
                    placeholder="1000"
                    value={newEndpoint.rate_limits?.per_hour || ''}
                    onChange={(e) => setNewEndpoint({ 
                      ...newEndpoint, 
                      rate_limits: { 
                        ...newEndpoint.rate_limits, 
                        per_hour: e.target.value ? parseInt(e.target.value) : undefined 
                      }
                    })}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-rate-day" className="text-xs">{t('products.perDay')}</Label>
                  <Input
                    id="edit-rate-day"
                    type="number"
                    placeholder="10000"
                    value={newEndpoint.rate_limits?.per_day || ''}
                    onChange={(e) => setNewEndpoint({ 
                      ...newEndpoint, 
                      rate_limits: { 
                        ...newEndpoint.rate_limits, 
                        per_day: e.target.value ? parseInt(e.target.value) : undefined 
                      }
                    })}
                  />
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="edit-skip-apisix-sync"
                checked={newEndpoint.skip_apisix_sync || false}
                onCheckedChange={(checked) => setNewEndpoint({ ...newEndpoint, skip_apisix_sync: !!checked })}
              />
              <div className="space-y-1">
                <Label htmlFor="edit-skip-apisix-sync" className="font-medium">
                  {t('products.skipGatewaySync', 'Skip API gateway sync')}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t('products.skipGatewaySyncDescription', 'Store this endpoint without queuing an APISIX sync job')}
                </p>
              </div>
            </div>

            <div>
              <Label htmlFor="edit-description">{t('products.descriptionOptional')}</Label>
              <Textarea
                id="edit-description"
                placeholder={t('products.describeEndpoint')}
                value={newEndpoint.description}
                onChange={(e) => setNewEndpoint({ ...newEndpoint, description: e.target.value })}
                className="mt-1"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsEditModalOpen(false);
              setEditingEndpoint(null);
              setNewEndpoint({
                gateway_path: '',
                original_url: '',
                methods: [],
                rate_limits: undefined,
                description: '',
                is_active: true
              });
            }}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleUpdateEndpoint}
              disabled={!newEndpoint.gateway_path || !newEndpoint.original_url || !newEndpoint.methods?.length || updateEndpointMutation.isPending}
            >
              {updateEndpointMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              {t('products.updateEndpoint')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Endpoint Modal */}
      <Dialog open={isTestModalOpen} onOpenChange={setIsTestModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('products.testApiEndpoint')}</DialogTitle>
            <DialogDescription>
              {t('products.testEndpointDescription')}
            </DialogDescription>
          </DialogHeader>
          
          {testingEndpoint && (
            <div className="space-y-6">
              {/* Request Configuration */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                  <span className="text-sm font-medium">{t('products.gatewayUrl')}</span>
                  <code className="text-sm flex-1">
                    {t('products.gatewayUrlExample', {
                      base: 'https://api.florentin.com',
                      path: testingGatewayPath ?? '',
                      defaultValue: `https://api.florentin.com${testingGatewayPath ?? ''}`,
                    })}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopyGatewayPath(testingGatewayPath)}
                    className="h-6 px-1"
                    disabled={!testingGatewayPath}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('products.httpMethod')}</Label>
                    <Select
                      value={testRequest.method}
                      onValueChange={(value) => setTestRequest({ ...testRequest, method: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {testingMethodOptions.map(method => (
                          <SelectItem key={method} value={method}>{method}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>{t('products.authentication')}</Label>
                    <div className="mt-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="use-test-key"
                          checked={testRequest.useTestKey}
                          onCheckedChange={(checked) => setTestRequest({ 
                            ...testRequest, 
                            useTestKey: checked as boolean,
                            apiKey: checked ? 'test_key_abc123xyz' : ''
                          })}
                        />
                        <Label htmlFor="use-test-key" className="text-sm font-normal">
                          {t('products.useTestApiKey')}
                        </Label>
                      </div>
                      {!testRequest.useTestKey && (
                        <Input
                          placeholder={t('products.enterApiKey')}
                          value={testRequest.apiKey}
                          onChange={(e) => setTestRequest({ ...testRequest, apiKey: e.target.value })}
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Headers */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>{t('products.headers')}</Label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addHeader}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      {t('products.addHeader')}
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {testRequest.headers.map((header, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          placeholder={t('products.headerName')}
                          value={header.key}
                          onChange={(e) => updateHeader(index, 'key', e.target.value)}
                          className="flex-1"
                        />
                        <Input
                          placeholder={t('products.headerValue')}
                          value={header.value}
                          onChange={(e) => updateHeader(index, 'value', e.target.value)}
                          className="flex-1"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeHeader(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Request Body */}
                {['POST', 'PUT', 'PATCH'].includes(testRequest.method) && (
                  <div>
                    <Label htmlFor="request-body">{t('products.requestBody')}</Label>
                    <Textarea
                      id="request-body"
                      placeholder='{"key": "value"}'
                      value={testRequest.body}
                      onChange={(e) => setTestRequest({ ...testRequest, body: e.target.value })}
                      className="mt-1 font-mono text-sm"
                      rows={6}
                    />
                  </div>
                )}
              </div>

              {/* Execute Button */}
              <div className="flex justify-center">
                <Button
                  onClick={handleExecuteTest}
                  disabled={isTestLoading || !testRequest.apiKey}
                  className="min-w-[200px]"
                >
                  {isTestLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('products.testing')}
                    </>
                  ) : (
                    <>
                      <Play className="mr-2 h-4 w-4" />
                      {t('products.executeTest')}
                    </>
                  )}
                </Button>
              </div>

              {/* Response Section */}
              {testResponse && (
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CardTitle className="text-base">{t('products.response')}</CardTitle>
                        <Badge 
                          variant={testResponse.status < 300 ? "default" : testResponse.status < 400 ? "secondary" : "destructive"}
                        >
                          {testResponse.status} {testResponse.statusText}
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          {t('products.responseTimeMs', {
                            time: testResponse.time,
                            defaultValue: `${testResponse.time}ms`,
                          })}
                        </span>
                      </div>
                      {testResponse.status < 300 && (
                        <Check className="h-5 w-5 text-green-600" />
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Tabs defaultValue="body" className="w-full">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="body">{t('products.body')}</TabsTrigger>
                        <TabsTrigger value="headers">{t('products.headers')}</TabsTrigger>
                      </TabsList>
                      <TabsContent value="body" className="mt-4">
                        <pre className="p-4 bg-muted rounded-lg overflow-x-auto text-sm">
                          {JSON.stringify(testResponse.body, null, 2)}
                        </pre>
                      </TabsContent>
                      <TabsContent value="headers" className="mt-4">
                        <div className="space-y-2">
                          {Object.entries(testResponse.headers).map(([key, value]) => (
                            <div key={key} className="flex gap-2 text-sm">
                              <span className="font-medium min-w-[200px]">{key}:</span>
                              <span className="text-muted-foreground">{value}</span>
                            </div>
                          ))}
                        </div>
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsTestModalOpen(false)}>
              {t('products.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.areYouSure')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('common.deleteEndpointConfirmation')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setEndpointToDelete(null)}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={deleteEndpointMutation.isPending} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deleteEndpointMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
